import { parseMcpGatewaySentinel } from '../../contracts/shared/file-path-labels';
import { GATEWAY_LATEST_OCCURRENCE, GATEWAY_SECRET_KINDS, } from '../../data/alert-risk-text';
import { closeSentence, MAX_MATCHED_LENGTH, fallbackOf, fillRiskTemplate, isNewerVersion, partsOf, present, riskCopy, sourceField, summaryClause, UNKNOWN_VERSION, } from './common';
// MCP servers and the MCP gateway (sheet rows 2, 5, 6, 59, 61–66, 105).
const OPTIONAL = new Set(['via', 'summary', 'fix']);
/** The `| Dependency Path: a → b → c` tail of an MCP dependency location. */
function dependencyPathOf(location) {
    if (location == null || location.length > MAX_MATCHED_LENGTH)
        return [];
    const match = location.match(/ \| Dependency Path: (.+)$/);
    return match?.[1] == null ? [] : match[1].split(' → ').map(s => s.trim());
}
/**
 * Row 2. Needs `alert_source_details` (source details); flag off renders the
 * static copy, with nothing rebuilt from the description (Security decision 9).
 */
export function resolveMcpVulnerability(alert) {
    const copy = riskCopy('enableDependenciesScan::vulnerability');
    const details = alert.sourceDetails;
    const osv = details?.kind === 'mcp' ? details.osvComponent : undefined;
    const name = present(osv?.name);
    const advisoryId = present(osv?.cveId) ??
        present(details?.kind === 'mcp' ? details.osvId : undefined);
    if (details?.kind !== 'mcp' ||
        osv == null ||
        name == null ||
        advisoryId == null) {
        return fallbackOf(copy);
    }
    const server = sourceField(details, 'name');
    // V5 interim rule: drop the root only when it is the server itself.
    const path = dependencyPathOf(alert.location).slice(0, -1);
    if (path[0] != null && path[0] === server)
        path.shift();
    const version = present(osv.version);
    const checked = version != null && version !== UNKNOWN_VERSION;
    const fixedVersion = present(osv.fixedVersion);
    const template = checked
        ? copy.whatHappened
        : copy.whatHappened.replace('Kirin checked version {version}, which is affected by', 'It is affected by');
    const summary = summaryClause(osv.summary);
    const what = fillRiskTemplate(template.replace('{: summary}', '{summary}'), {
        subject: server == null || server.toLowerCase() === 'unknown'
            ? 'An MCP server'
            : `The MCP server ${server}`,
        name,
        via: path.length === 0 ? '' : ` through ${path.join(' → ')}`,
        version,
        advisoryId,
        summary,
    }, OPTIONAL);
    const why = fillRiskTemplate(copy.whyItMatters, {
        fix: checked &&
            fixedVersion != null &&
            isNewerVersion(fixedVersion, version, osv.ecosystem)
            ? `; version ${fixedVersion} includes a fix`
            : '',
    }, OPTIONAL);
    return partsOf(copy, what == null ? null : closeSentence(what, summary), why);
}
const TOOL_LOCATION = /^Tool: "(.+)" \(Server: (.*)\)$/;
/**
 * Rows 5 and 6. The gateway's tool-description surface (S2) is static; a
 * non-gateway finding (S1) names the tool and server from the location's first
 * line. The rule reason is not repeated: "Detected" already shows it.
 */
export function resolveToolDescription(alert, mode) {
    const sentinel = parseMcpGatewaySentinel(alert.filePath ?? '');
    if (mode === 'sections' && sentinel?.surface === 'tool-description') {
        return fallbackOf(riskCopy('enableToolDescriptionsScan::suspicious_description#S2'));
    }
    const copy = riskCopy('enableToolDescriptionsScan::suspicious_description#S1');
    const firstLine = (alert.location ?? '').split('\n')[0]?.trim() ?? '';
    const match = firstLine.length > MAX_MATCHED_LENGTH
        ? null
        : firstLine.match(TOOL_LOCATION);
    const tool = present(match?.[1]);
    if (match == null || tool == null) {
        return fallbackOf(copy);
    }
    const server = present(match[2]);
    return partsOf(copy, fillRiskTemplate(copy.whatHappened, {
        tool,
        serverPhrase: server == null || server.toLowerCase() === 'unknown'
            ? 'an MCP server'
            : `server ${server}`,
    }), copy.whyItMatters);
}
const GATEWAY_SURFACE_VARIANT = {
    'tool-args': 'G1',
    'tool-result': 'G2',
    'prompt-args': 'G3',
    'prompt-result': 'G4',
};
function isGatewaySecretSurface(value) {
    return value in GATEWAY_SURFACE_VARIANT;
}
/**
 * Rows 61–66. The surface and tool come from the latest occurrence's filePath,
 * so the surface template is used only when the group has one occurrence; a
 * larger group gets the latest-occurrence form, and an unknown count gets G0.
 */
export function resolveGatewaySecret(alert, mode) {
    const base = riskCopy('secretGatewayContent::secret-leakage#base');
    const sentinel = parseMcpGatewaySentinel(alert.filePath ?? '');
    if (mode === 'text' || sentinel == null) {
        return fallbackOf(base);
    }
    if (!isGatewaySecretSurface(sentinel.surface)) {
        return fallbackOf(riskCopy('secretGatewayContent::secret-leakage#G0'));
    }
    const count = alert.occurrenceCount;
    if (count == null) {
        return fallbackOf(riskCopy('secretGatewayContent::secret-leakage#G0'));
    }
    const surface = sentinel.surface;
    const copy = riskCopy(`secretGatewayContent::secret-leakage#${GATEWAY_SURFACE_VARIANT[surface]}`);
    const kind = alert.description.trim();
    const tool = present(sentinel.tool);
    const facts = {
        kindPhrase: GATEWAY_SECRET_KINDS.has(kind)
            ? `the ${kind} format`
            : 'a credential pattern',
        gatewayToolPhrase: tool == null ? 'an MCP tool' : `the MCP tool "${tool}"`,
    };
    if (count === 1) {
        return partsOf(copy, fillRiskTemplate(copy.whatHappened, facts), copy.whyItMatters);
    }
    return partsOf(copy, fillRiskTemplate(`${GATEWAY_LATEST_OCCURRENCE.lead} ${GATEWAY_LATEST_OCCURRENCE.surfaces[surface]}`, facts), GATEWAY_LATEST_OCCURRENCE.whyItMatters);
}
/** Row 105: names the server from the group location (its config name). */
export function resolveNewMcpServer(alert) {
    const copy = riskCopy('mcpServersNewServer::new_mcp_server');
    return partsOf(copy, fillRiskTemplate(copy.whatHappened, {
        configName: present(alert.location),
    }), copy.whyItMatters);
}
//# sourceMappingURL=mcp.js.map